import { MouseEvent, useContext } from 'react'

import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'

import { PurchaseLicense } from '@components/Submission/Card/Modal/PurchaseLicense/PurchaseLicense'
import ActionIcon from '@components/UI/ActionIcon'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { CampaignContext } from '@context/Campaign'
import { useIntl } from '@context/Internationalization/useIntl'
import useMultiFrameControls from '@context/MultiframeControls/useMultiframControl'
import { PopupMenuContext } from '@context/PopupMenu'
import { EventTrackingNames, SubmissionStatuses } from '@enums'
import { useModal } from '@hooks/useModal'
import { IApplicationState } from '@store'
import { selectIsUnlockedCampaign } from '@store/campaigns'
import { selectCampaignId } from '@store/router'
import {
  selectActiveSubmissionMediaObject,
  selectSubmission,
  selectSubmissionMediaObjectFrameNumber,
} from '@store/submissions'
import Theme from '@theme'
import { pxToRem } from '@utils/pxToRem'
import ActionBarIconApproveContentPopupMenu from '../Approve/ContentPopupMenu'

interface IProps {
  submissionId: number
}

export const ActionBarIconPurchaseLicense = ({ submissionId }: IProps) => {
  const { intl } = useIntl()
  const { showModal } = useModal()
  const { activeFrameNumber } = useMultiFrameControls()
  const match = useRouteMatch()

  const { campaign } = useContext(CampaignContext)
  const { showPopup } = useContext(PopupMenuContext)

  const { frameNumber, mediaObjectId, campaignId, submission, isUnlockedCampaign } = useSelector(
    (state: IApplicationState) => {
      const { id: mediaObjectId } = selectActiveSubmissionMediaObject(
        state,
        submissionId,
        activeFrameNumber,
      )
      const campaignId = selectCampaignId(match) as number

      return {
        frameNumber: selectSubmissionMediaObjectFrameNumber(state, submissionId, mediaObjectId),
        mediaObjectId,
        campaignId,
        submission: selectSubmission(state, submissionId),
        isUnlockedCampaign: selectIsUnlockedCampaign(state, campaignId),
      }
    },
    shallowEqual,
  )

  const isHighlighted = submission.status === SubmissionStatuses?.PreApproved

  const onClick = ({ target }: MouseEvent<HTMLDivElement>) => {
    if (
      !isUnlockedCampaign &&
      campaign.preapproval_allowed &&
      submission.status === SubmissionStatuses.Pending
    ) {
      const targetElement = target as HTMLElement
      const { left, top, width } = targetElement.getBoundingClientRect()

      showPopup(
        <ActionBarIconApproveContentPopupMenu
          submissionId={submissionId}
          frameNumber={frameNumber}
        />,
        {
          x: pxToRem(left + width),
          y: pxToRem(top),
        },
      )
      return
    }

    showModal(PurchaseLicense, {
      submissionId,
      frameNumber,
      approvalType: 'final-approval',
    })
  }

  return (
    <ConnectedTrackedRoute
      eventName={EventTrackingNames.ViewLicensingOptions}
      campaignId={campaignId}
      submissionId={submissionId}
      mediaObjectId={mediaObjectId}
    >
      <ActionIcon
        glyph="approve-submission"
        color={Theme.grey900Color}
        size={1.5}
        onClick={onClick}
        tooltip={intl.formatMessage({ id: 'submission.card.action.licensecontent.licenseContent' })}
        elementName="purchase-license"
        highlighted={isHighlighted}
      />
    </ConnectedTrackedRoute>
  )
}

export default ActionBarIconPurchaseLicense
